import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import FormControlLabel from '@mui/material/FormControlLabel';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Typography from '@mui/material/Typography';
import { BillingLineSide } from '@treadinc/horizon-api-spec';
import { t } from 'i18next';
import { useCallback, useEffect, useState } from 'react';

import DataGridPager from '~components/DataGrid/DataGridPager';
import Dialog from '~components/Dialog/Dialog';
import DialogActions from '~components/Dialog/DialogActions';
import DialogContent from '~components/Dialog/DialogContent';
import DialogHeader from '~components/Dialog/DialogHeader';
import {
  SmallButton,
  SmallLoadingButton,
} from '~components/Order/ordersDispatchStyledComponents';
import { BillingLine } from '~hooks/useBillingLines/models';
import { useCompanyCurrency } from '~hooks/useCompanyCurrency/useCompanyCurrency';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { Settlement } from '~hooks/useSettlements/models';
import useSettlements from '~hooks/useSettlements/useSettlements';
import { PaginationLink } from '~services/pagination';
import reportRequestError from '~services/reportRequestError';
import { useStores } from '~store';
import {
  formatSettlementTotal,
  getFriendlySettlementId,
} from '~utils/settlements/settlement-utils';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { categoryBySide } from '../chargeSelection';

// A party seldom has more open drafts than fit a short list; the pager reaches the rest
const DRAFTS_PAGE_LIMIT = 10;

type BillingLineParty = BillingLine['scope']['party'];

interface AddToDraftSettlementDialogProps {
  isBusy: boolean;
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (settlement: Settlement) => void;
  party: BillingLineParty;
  side: BillingLineSide;
}

export default function AddToDraftSettlementDialog({
  isBusy,
  isOpen,
  onClose,
  onConfirm,
  party,
  side,
}: AddToDraftSettlementDialogProps) {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const { currencyFormatter } = useCompanyCurrency();
  const { getDraftSettlementsForParty } = useSettlements();
  const [selectedSettlement, setSelectedSettlement] = useState<Settlement | null>(null);
  const [hasDraftsReadFailed, setHasDraftsReadFailed] = useState(false);

  const { goToPage, isFetching, pagination, rows, startOver } =
    useCursorPagination<Settlement>({
      fetchPage: (pageParams) =>
        getDraftSettlementsForParty({
          ...pageParams,
          companyId,
          party,
          side,
        }),
      limit: DRAFTS_PAGE_LIMIT,
      onFetchError: (error) => {
        setHasDraftsReadFailed(true);
        reportRequestError(error, t('error_messages.settlements.failed_to_fetch_drafts'));
      },
    });

  const readDraftsFromStart = useCallback(() => {
    setSelectedSettlement(null);
    setHasDraftsReadFailed(false);
    void startOver();
  }, [startOver]);

  useEffect(() => {
    if (isOpen) {
      readDraftsFromStart();
    }
  }, [isOpen, party.id, party.type, readDraftsFromStart, side]);

  const handleClose = useCallback(() => {
    if (isBusy) {
      return;
    }

    onClose();
  }, [isBusy, onClose]);

  const goToDraftsPage = useCallback(
    (link?: PaginationLink) => {
      setSelectedSettlement(null);
      setHasDraftsReadFailed(false);
      void goToPage(link);
    },
    [goToPage],
  );

  const handleConfirm = useCallback(() => {
    if (selectedSettlement) {
      onConfirm(selectedSettlement);
    }
  }, [onConfirm, selectedSettlement]);

  const showsReadFailure = !isFetching && hasDraftsReadFailed;
  const hasNoDrafts = !isFetching && !hasDraftsReadFailed && rows.length === 0;

  return (
    <Dialog isOpen={isOpen} onClose={handleClose} preventCloseOnBackdropClick>
      <DialogHeader onClose={handleClose}>
        {t('approvals.approve_charges.add_to_draft.title')}
      </DialogHeader>

      <DialogContent>
        <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1.5 }}>
          {t('approvals.approve_charges.add_to_draft.description', {
            interpolation: { escapeValue: false },
            party: party.name ?? t('approvals.approve_charges.selection.no_party'),
          })}
        </Typography>

        <RadioGroup
          value={selectedSettlement?.id ?? ''}
          onChange={(_event, settlementId) =>
            setSelectedSettlement(
              rows.find((settlement) => settlement.id === settlementId) ?? null,
            )
          }
        >
          {rows.map((settlement) => (
            <FormControlLabel
              key={settlement.id}
              control={<Radio size="small" />}
              disabled={isBusy}
              label={t('approvals.approve_charges.add_to_draft.draft', {
                count: settlement.jobsCount,
                interpolation: { escapeValue: false },
                name: getFriendlySettlementId(settlement, categoryBySide[side]),
                total: formatSettlementTotal(settlement, currencyFormatter),
              })}
              value={settlement.id}
            />
          ))}
        </RadioGroup>

        {isFetching && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={20} />
          </Box>
        )}
        {showsReadFailure && (
          <Box sx={{ alignItems: 'center', display: 'flex', gap: 1 }}>
            <Typography variant="body2" sx={{ color: 'error.main' }}>
              {t('error_messages.settlements.failed_to_fetch_drafts')}
            </Typography>
            <SmallButton
              disabled={isBusy}
              onClick={readDraftsFromStart}
              variant="outlined"
            >
              {t('actions.retry')}
            </SmallButton>
          </Box>
        )}
        {hasNoDrafts && (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('approvals.approve_charges.add_to_draft.empty')}
          </Typography>
        )}

        <Box sx={{ mt: 1.5 }}>
          <DataGridPager
            isFetching={isFetching || isBusy}
            onNavigate={goToDraftsPage}
            pagination={pagination}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <SmallButton
          color="secondary"
          disabled={isBusy}
          onClick={handleClose}
          variant="outlined"
        >
          {t('actions.cancel')}
        </SmallButton>

        <SmallLoadingButton
          disabled={isBusy || !selectedSettlement}
          loading={isBusy}
          onClick={handleConfirm}
        >
          {t('approvals.approve_charges.add_to_draft.confirm')}
        </SmallLoadingButton>
      </DialogActions>
    </Dialog>
  );
}
